import moment from "moment";
import { useEffect, useState } from "react";
import useCache from "../../../../hooks/use-cache.hook";
import TextButton from "../../../../_components/atoms/text-button";
import Pagination from "../../../../_components/pagination.component";
import { EmailService } from "../../../../_service";
import { MemberTableRowShimmer } from "../../invited-users-list";

/**
 * @description Display list of sent mails.
 * @param {Object} mail - Auth mail object
 * @param {Object} community - Current community object
 * @param {Array<Object>} groups - List of groups
 * @param {boolean} refreshMailList - Refresh list of sent mails if value changed [true, false]
 * @param {Object} communityPac - Current community pac object
 * @param {Function} addToast - Add toast function
 * @param {boolean} setDisplaySendMail - Set display send mail state
 */
export default function BulkMailListPage({
  user,
  community,
  groups,
  refreshMailList,
  setDisplaySendMail,
  setMailToDisplay,
}) {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [mailList, setMailList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { cache, saveCache } = useCache();

  useEffect(() => {
    if (community && user) {
      fetchEmailList(page);
    }
  }, [page, community, user]);

  useEffect(() => {
    if (community && user) {
      fetchEmailList(1, false);
    }
  }, [refreshMailList]);

  function fetchEmailList(page, fromCache = true) {
    if (isLoading) {
      return;
    }
    if (fromCache && cache[page]) {
      setMailList(cache[page]);
      setPage(page);
      return;
    }
    setIsLoading(true);
    EmailService.getBulkMailList(user, community.id, "", page)
      .then(({ data, total }) => {
        setMailList(data);
        setPage(page);
        setTotal(total);
        saveCache(page, data);
      })

      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function getGroupNameFromId(id) {
    let groupName = "-";
    if (!id) {
      return groupName;
    }
    groups.forEach((group) => {
      if (group.id === id) {
        groupName = group.name;
      }
    });
    return groupName;
  }

  return (
    <div className="BulkMailListPage theme-bg-surface p-4 mt-1">
      <div className="flex flex-col items-center place-content-center space-y-2 border border-dotted theme-border-default h-32 theme-bg-default rounded ">
        <TextButton
          label="Send Mail"
          onClick={() => {
            setDisplaySendMail(true);
          }}
        />
        <p className="theme-text-primary">
          Send bulk mail to all the members of community or selected groups
        </p>
      </div>
      {/* Table */}
      <div className="w-full mt-8 overflow-x-auto">
        <table className="table-auto min-w-full text-sm theme-text-subtitle-1 border theme-border-default w-full rounded">
          <thead className="text-xs text-left theme-text-headline-1 uppercase theme-bg-default">
            <tr>
              <th scope="col" className="px-6 pr-20 py-3">
                Date
              </th>
              <th scope="col" className="px-6 py-3">
                Subject
              </th>
              <th scope="col" className="px-6 w-36 py-3">
                Invited
              </th>
              <th scope="col" className="px-6 py-3">
                Recipients
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--theme-border-color)]">
            {isLoading ? (
              [...Array(10)].map((_, index) => (
                <MemberTableRowShimmer key={index} />
              ))
            ) : (
              <>
                {mailList.length > 0 ? (
                  mailList.map((mail, index) => {
                    return (
                      <tr
                        key={index}
                        onClick={() => {
                          setMailToDisplay(mail);
                          setDisplaySendMail(true);
                        }}
                        className="theme-bg-surface  theme-border-default hover:theme-bg-default cursor-pointer ">
                        <td className="px-6 py-2 theme-text-subtitle-1">
                          {moment(mail.createdAt).fromNow()}
                        </td>
                        <td className="px-6 py-2 theme-text-subtitle-1">
                          {mail.subjectLine ?? "-"}
                        </td>
                        <td className="px-6 py-2 theme-text-subtitle-1">
                          {mail.group == null
                            ? community.name
                            : getGroupNameFromId(mail.group)}
                        </td>
                        <td className="px-6 py-2 font-medium theme-text-headline-1 ">
                          {mail.recipientCount > 0 ? (
                            <>
                              {mail.recipientCount}{" "}
                              {mail.recipientCount > 0 ? "users" : "User"}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr
                    className=" items-center justify-center place-content-center text-center h-32 w-full"
                    colSpan="5">
                    <td></td>
                    <td colSpan="2">No Mail</td>
                    <td></td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div>
        <Pagination
          className="pagination-bar justify-center"
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(val) => {
            fetchEmailList(val);
          }}
        />
      </div>
    </div>
  );
}
